"use client"

import type { FC, PointerEvent, ReactElement, ReactNode, SVGProps } from "react"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { IconChevronRightOutline18 as ChevronRight } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../../icons"
import { Command, CommandGroup, CommandItem, CommandList } from "./command"

// The popover stays overflow-visible so the sub-panel can sit beside it, so the list itself has to fit the room Radix leaves under the trigger (minus the filter input).
export const NESTED_COMMAND_LIST_CLASS =
	"max-h-[min(70dvh,400px,calc(var(--radix-popover-content-available-height,100dvh)-3.5rem))]"

const GRACE_PERIOD_MS = 300

interface Point {
	x: number
	y: number
}

function isPointInPolygon({ x, y }: Point, polygon: Point[]) {
	let inside = false
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const a = polygon[i]!
		const b = polygon[j]!
		if (
			a.y > y !== b.y > y &&
			x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x
		)
			inside = !inside
	}
	return inside
}

export interface NestedCommandGroup<T extends string = string> {
	type: T
	label: string
	Icon?: LucideIcon | IconType
	badge?: ReactNode
}

export interface NestedCommandProps<T extends string = string> {
	groups: NestedCommandGroup<T>[]
	activeGroup: T | null
	onGroupChange: (group: T | null) => void
	/** Content rendered in the sub-panel for the active group */
	renderSubContent: (group: T) => ReactNode
	/** Content rendered above the group list (e.g. a search input) */
	header?: ReactNode
	/** When provided, replaces the group list in the main panel (e.g. search results) */
	children?: ReactNode
	/** Width of the main (left) panel. Default: "w-48" */
	mainPanelClassName?: string
	/** Width of the sub (right) panel. Default: "w-64" */
	subPanelClassName?: string
	/** Which side the sub-panel opens on. Default: "right". */
	subPanelSide?: "left" | "right"
	/** Hover close delay in ms. Default: 150 */
	closeDelay?: number
}

/**
 * A two-panel command menu where hovering a group in the left panel
 * opens a sub-panel to the right, positioned next to the hovered item.
 *
 * Usage:
 * ```tsx
 * <NestedCommand
 *   groups={[
 *     { type: "status", label: "Status", Icon: CircleDot },
 *     { type: "assignee", label: "Assignee", Icon: UserPen },
 *   ]}
 *   activeGroup={activeGroup}
 *   onGroupChange={setActiveGroup}
 *   renderSubContent={(group) => <MyFilterItems type={group} />}
 * />
 * ```
 */
export const NestedCommand = memo(function NestedCommand<
	T extends string = string,
>({
	groups,
	activeGroup,
	onGroupChange,
	renderSubContent,
	header,
	children,
	mainPanelClassName = "w-48",
	subPanelClassName = "w-64",
	subPanelSide = "right",
	closeDelay = 150,
}: NestedCommandProps<T>) {
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const subPanelRef = useRef<HTMLDivElement>(null)
	// Like Radix's submenu grace area: while the pointer heads from the open row toward the sub-panel, rows it crosses don't steal it.
	const graceAreaRef = useRef<Point[] | null>(null)
	const graceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const [subPanelTop, setSubPanelTop] = useState(0)

	const clearCloseTimeout = useCallback(() => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current)
			closeTimeoutRef.current = null
		}
	}, [])

	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current)
			}
			if (graceTimeoutRef.current) {
				clearTimeout(graceTimeoutRef.current)
			}
		}
	}, [])

	const handleGroupEnter = useCallback(
		(type: T, e?: PointerEvent) => {
			clearCloseTimeout()
			if (activeGroup !== type) {
				onGroupChange(type)
			}
			if (e && containerRef.current) {
				const item = (e.currentTarget as HTMLElement).closest("[cmdk-item]")
				if (item) {
					const containerRect = containerRef.current.getBoundingClientRect()
					const itemRect = item.getBoundingClientRect()
					setSubPanelTop(itemRect.top - containerRect.top)
				}
			}
		},
		[activeGroup, onGroupChange, clearCloseTimeout],
	)

	const handleGroupLeave = useCallback(
		(type: T, e: PointerEvent) => {
			const subPanel = subPanelRef.current
			if (activeGroup !== type || !subPanel) return
			const rect = subPanel.getBoundingClientRect()
			const toRight = subPanelSide === "right"
			const near = toRight ? rect.left : rect.right
			const far = toRight ? rect.right : rect.left
			graceAreaRef.current = [
				{ x: e.clientX + (toRight ? -5 : 5), y: e.clientY },
				{ x: near, y: rect.top },
				{ x: far, y: rect.top },
				{ x: far, y: rect.bottom },
				{ x: near, y: rect.bottom },
			]
			if (graceTimeoutRef.current) clearTimeout(graceTimeoutRef.current)
			graceTimeoutRef.current = setTimeout(() => {
				graceAreaRef.current = null
			}, GRACE_PERIOD_MS)
		},
		[activeGroup, subPanelSide],
	)

	const handleGroupMove = useCallback(
		(type: T, e: PointerEvent) => {
			const area = graceAreaRef.current
			if (area && isPointInPolygon({ x: e.clientX, y: e.clientY }, area)) {
				// Also keeps cmdk from moving its highlight to the crossed row.
				e.stopPropagation()
				return
			}
			handleGroupEnter(type, e)
		},
		[handleGroupEnter],
	)

	const handlePanelLeave = useCallback(() => {
		clearCloseTimeout()
		closeTimeoutRef.current = setTimeout(() => {
			onGroupChange(null)
		}, closeDelay)
	}, [onGroupChange, closeDelay, clearCloseTimeout])

	return (
		<div className="relative" ref={containerRef}>
			{/* Main panel — group list */}
			<div
				className={cn("py-1", mainPanelClassName)}
				onPointerEnter={clearCloseTimeout}
				onPointerLeave={handlePanelLeave}
			>
				<Command shouldFilter={false}>
					{header}
					{children ? (
						children
					) : (
						<CommandList className={NESTED_COMMAND_LIST_CLASS}>
							<CommandGroup>
								{groups.map((group) => (
									<CommandItem
										key={group.type}
										onSelect={() => handleGroupEnter(group.type)}
										// cmdk sets its own onPointerMove on the item after spreading props, so ours runs in the capture phase.
										onPointerMoveCapture={(e) => handleGroupMove(group.type, e)}
										onPointerLeave={(e) => handleGroupLeave(group.type, e)}
										Icon={group.Icon}
										className={cn(
											activeGroup === group.type &&
												"bg-gray-100 dark:bg-gray-800",
										)}
									>
										<div className="flex w-full items-center justify-between">
											<span>{group.label}</span>
											<div className="flex items-center gap-1">
												{group.badge}
												<ChevronRight className="h-3 w-3 text-tertiary" />
											</div>
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					)}
				</Command>
			</div>

			{/* Sub-panel — content for active group */}
			{activeGroup && (
				<div
					ref={subPanelRef}
					className={cn(
						"absolute z-50 rounded-lg border border-gray-200 bg-gray-25 py-1 shadow-lg dark:border-gray-700/70 dark:bg-gray-850",
						subPanelSide === "left" ? "right-full mr-1" : "left-full ml-1",
						subPanelClassName,
					)}
					style={{
						top: subPanelTop,
						transition: "top 0.12s cubic-bezier(0.23, 1, 0.32, 1)",
					}}
					onPointerEnter={clearCloseTimeout}
					onPointerLeave={handlePanelLeave}
				>
					{renderSubContent(activeGroup)}
				</div>
			)}
		</div>
	)
}) as <T extends string = string>(props: NestedCommandProps<T>) => ReactElement

type LucideIcon = FC<SVGProps<SVGSVGElement>>
